import { memo, useRef } from "react";
import ReactECharts from "echarts-for-react";

function PriceHistoryChart({ priceHistory, chartType, timeRange, isMaximized = false }) {
  const chartRef = useRef(null);

  // Calculate SMA on the full history array (including safety buffers) to avoid partial lines
  const calculateSMA = (data, period) => {
    const sma = [];
    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        sma.push(null);
      } else {
        let sum = 0;
        for (let j = 0; j < period; j++) {
          sum += data[i - j].close || 0;
        }
        sma.push(Number((sum / period).toFixed(2)));
      }
    }
    return sma;
  };

  const fullDma50Data = calculateSMA(priceHistory, 50);
  const fullDma200Data = calculateSMA(priceHistory, 200);

  // Filter out buffer points so they aren't plotted or visible in the selected range
  const visibleIndices = [];
  priceHistory.forEach((item, index) => {
    if (item.is_visible !== false) {
      visibleIndices.push(index);
    }
  });

  const useFiltering = visibleIndices.length > 0 && visibleIndices.length < priceHistory.length;

  const visiblePriceHistory = useFiltering
    ? visibleIndices.map(idx => priceHistory[idx])
    : priceHistory;

  const dma50Data = useFiltering
    ? visibleIndices.map(idx => fullDma50Data[idx])
    : fullDma50Data;

  const dma200Data = useFiltering
    ? visibleIndices.map(idx => fullDma200Data[idx])
    : fullDma200Data;

  const allPrices = visiblePriceHistory.flatMap(item => {
    const prices = [item.open, item.close];
    if (item.high) prices.push(item.high);
    if (item.low) prices.push(item.low);
    return prices;
  }).filter(price => price !== null && price !== undefined && !isNaN(price));

  if (allPrices.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <p>No valid price data available</p>
      </div>
    );
  }

  const parsedDates = visiblePriceHistory.map(item => new Date(item.date));

  const getVisibleSpanDays = () => {
    const chart = chartRef.current?.getEchartsInstance?.();
    if (!chart) return null;
    const opt = chart.getOption();
    const dz = opt?.dataZoom;
    if (!dz || dz.length === 0) return null;
    const start = dz[0].startValue ?? dz[0].start;
    const end = dz[0].endValue ?? dz[0].end;
    let startIdx, endIdx;
    if (typeof start === 'number' && start <= 100 && typeof end === 'number' && end <= 100) {
      startIdx = Math.floor((start / 100) * (visiblePriceHistory.length - 1));
      endIdx = Math.ceil((end / 100) * (visiblePriceHistory.length - 1));
    } else {
      startIdx = start ?? 0;
      endIdx = end ?? visiblePriceHistory.length - 1;
    }
    startIdx = Math.max(0, startIdx);
    endIdx = Math.min(visiblePriceHistory.length - 1, endIdx);
    if (startIdx >= endIdx) return 0;
    const d0 = parsedDates[startIdx];
    const d1 = parsedDates[endIdx];
    return (d1 - d0) / (1000 * 60 * 60 * 24);
  };

  const getLabelLevel = (spanDays) => {
    if (spanDays === null) {
      const m = { '1D': 'time', '5D': 'day', '1M': 'day', '3M': 'day', '6M': 'month', 'YTD': 'month', '1Y': 'month', '5Y': 'year', 'All': 'year' };
      return m[timeRange] || 'month';
    }
    if (spanDays < 2) return 'time';
    if (spanDays < 45) return 'day';
    if (spanDays < 730) return 'month';
    if (spanDays < 1825) return 'quarter';
    return 'year';
  };

  let series = [];
  let legendData = [];

  if (chartType === "candle") {
    const candleData = visiblePriceHistory.map(item => [
      item.open || 0,
      item.close || 0,
      item.low !== null && item.low !== undefined ? item.low : Math.min(item.open || 0, item.close || 0),
      item.high !== null && item.high !== undefined ? item.high : Math.max(item.open || 0, item.close || 0)
    ]);

    series.push({
      name: "Price",
      type: "candlestick",
      data: candleData,
      itemStyle: {
        color: "#10b981",
        color0: "#ef4444",
        borderColor: "#10b981",
        borderColor0: "#ef4444"
      }
    });
    legendData = ["Price"];
  } else {
    series = [
      {
        name: "Open",
        type: "line",
        data: visiblePriceHistory.map(item => item.open),
        smooth: true,
        itemStyle: { color: "#3b82f6" },
        lineStyle: { color: "#3b82f6", width: 2 }
      },
      {
        name: "Close",
        type: "line",
        data: visiblePriceHistory.map(item => item.close),
        smooth: true,
        itemStyle: { color: "#10b981" },
        lineStyle: { color: "#10b981", width: 2 }
      }
    ];
    legendData = ["Open", "Close"];
  }

  // Append DMA series and legends
  series.push(
    {
      name: "50 DMA",
      type: "line",
      data: dma50Data,
      smooth: true,
      showSymbol: false,
      itemStyle: { color: "#ff79c6" }, // bright neon pink
      lineStyle: { color: "#ff79c6", width: 1.5 }
    },
    {
      name: "200 DMA",
      type: "line",
      data: dma200Data,
      smooth: true,
      showSymbol: false,
      itemStyle: { color: "#a855f7" }, // purple-500
      lineStyle: { color: "#a855f7", width: 1.5 }
    }
  );
  legendData.push("50 DMA", "200 DMA");

  const chartHeight = isMaximized ? "calc(100vh - 120px)" : "400px";

  const shouldShowLabel = (index, spanDays) => {
    const level = getLabelLevel(spanDays);
    const date = parsedDates[index];
    if (!date || isNaN(date.getTime())) return false;

    const prev = index > 0 ? parsedDates[index - 1] : null;

    switch (level) {
      case 'time': {
        if (index === 0) return true;
        return !prev || date.getHours() !== prev.getHours();
      }
      case 'day': {
        if (index === 0) return true;
        return !prev || date.getDate() !== prev.getDate();
      }
      case 'month': {
        if (index === 0) return true;
        return !prev || date.getMonth() !== prev.getMonth();
      }
      case 'quarter': {
        if (index === 0) return true;
        const isNewMonth = !prev || date.getMonth() !== prev.getMonth();
        const m = date.getMonth();
        const isQtr = (m === 2 || m === 5 || m === 8 || m === 11);
        return isNewMonth && isQtr;
      }
      case 'year': {
        if (index === 0) return true;
        return !prev || date.getFullYear() !== prev.getFullYear();
      }
      default:
        return true;
    }
  };

  const formatLabel = (index, spanDays) => {
    const level = getLabelLevel(spanDays);
    const date = parsedDates[index];
    if (!date || isNaN(date.getTime())) return '';

    const prev = index > 0 ? parsedDates[index - 1] : null;
    const isNewMonth = !prev || date.getMonth() !== prev.getMonth();
    const isNewYear = !prev || date.getFullYear() !== prev.getFullYear();

    switch (level) {
      case 'time': {
        const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        const isNewDay = !prev || date.getDate() !== prev.getDate();
        if (isNewDay || index === 0) {
          return timePart + '\n' + date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
        return timePart;
      }
      case 'day': {
        const day = date.getDate().toString();
        if (isNewMonth || index === 0) {
          return day + '\n' + date.toLocaleString('en-US', { month: 'short' });
        }
        return day;
      }
      case 'month': {
        const monthStr = date.toLocaleString('en-US', { month: 'short' });
        if (isNewYear || index === 0) {
          return monthStr + '\n' + date.getFullYear();
        }
        return monthStr;
      }
      case 'quarter': {
        const monthStr2 = date.toLocaleString('en-US', { month: 'short' });
        if (index === 0 || date.getMonth() === 2) {
          return monthStr2 + '\n' + date.getFullYear();
        }
        return monthStr2;
      }
      case 'year': {
        return date.getFullYear().toString();
      }
      default:
        return '';
    }
  };

  return (
    <ReactECharts
      ref={chartRef}
      option={{
        backgroundColor: "transparent",
        textStyle: { color: "#e5e7eb" },
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "cross" },
          formatter: function (params) {
            const rawDate = params[0].name;
            let formattedDate = rawDate;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
              const day = d.getDate();
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              const mon = months[d.getMonth()];
              const yr = d.getFullYear();
              formattedDate = `${day} ${mon} ${yr}`;
            }

            let result = `<b>${formattedDate}</b><br/>`;
            params.forEach(param => {
              if (param.seriesName === "Price") {
                const data = param.data;
                if (Array.isArray(data)) {
                  result += `Open: <b>$${data[1].toFixed(2)}</b><br/>` +
                            `Close: <b>$${data[2].toFixed(2)}</b><br/>` +
                            `Low: <b>$${data[3].toFixed(2)}</b><br/>` +
                            `High: <b>$${data[4].toFixed(2)}</b><br/>`;
                }
              } else if (param.value !== null && param.value !== undefined && !isNaN(param.value)) {
                result += `${param.seriesName}: <b>$${Number(param.value).toFixed(2)}</b><br/>`;
              }
            });
            return result;
          }
        },
        legend: {
          data: legendData,
          left: 'left',
          top: 4,
          textStyle: { color: "#e5e7eb" }
        },
        grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },
        dataZoom: [
          {
            type: "slider",
            show: false,
            xAxisIndex: [0],
            start: 0,
            end: 100,
            textStyle: { color: "#9ca3af" },
            borderColor: "#374151"
          },
          {
            type: "inside",
            xAxisIndex: [0],
            start: 0,
            end: 100,
            filterMode: 'filter'
          }
        ],
        animation: visiblePriceHistory.length < 500,
        xAxis: {
          type: "category",
          boundaryGap: chartType === "candle",
          data: visiblePriceHistory.map(item => item.date),
          axisLine: { lineStyle: { color: "#6b7280" } },
          axisTick: { alignWithLabel: true },
          axisLabel: {
            color: "#E0E0E0",
            fontSize: 13,
            rotate: 0,
            hideOverlap: true,
            interval: function (index) {
              const spanDays = getVisibleSpanDays();
              return shouldShowLabel(index, spanDays);
            },
            formatter: function (value, index) {
              const spanDays = getVisibleSpanDays();
              return formatLabel(index, spanDays);
            }
          }
        },
        yAxis: {
          type: "value",
          scale: true,
          axisLine: { lineStyle: { color: "#6b7280" } },
          axisLabel: {
            color: "#E0E0E0",
            fontSize: 13,
            formatter: (value) => Math.round(value).toLocaleString()
          },
          splitLine: { lineStyle: { color: "#374151" } }
        },
        series: series
      }}
      notMerge={true}
      style={{ height: chartHeight, width: "100%" }}
    />
  );
}

export default memo(PriceHistoryChart);
